import {
  useCallback,
  useEffect,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { currentLang, t } from '../i18n';
import { useConfig } from '../lib/config';
import { IBAN_WRONG, ibanOk, LEGAL_FORMS, zipOk } from './company';

// Formular-Bausteine für Registrierung und „Weitere Firma anmelden“ (Aussehen wie die Demo, .mapp .form).
// Fehler stehen direkt unter dem Feld; das erste fehlerhafte Feld bekommt den Fokus.
// Sprache: Bezeichnungen (label, placeholder) und Meldungen übergibt der Aufrufer schon übersetzt (mit t). Nur die
// Auswahl-Optionen übersetzt SelectField selbst, denn ihr deutscher Text ist zugleich der Wert für die API.

export type FormValues = Record<string, string>;
export type FormErrors = Record<string, string | undefined>;

export interface FormState {
  values: FormValues;
  errors: FormErrors;
  set(name: string, value: string): void;
  setErrors(e: FormErrors): void;
}

export function useForm(initial: FormValues): FormState {
  const [values, setValues] = useState<FormValues>(initial);
  const [errors, setErrors] = useState<FormErrors>({});
  const set = useCallback((name: string, value: string) => {
    setValues((v) => ({ ...v, [name]: value }));
    // Meldung verschwindet bei der nächsten Eingabe in diesem Feld
    setErrors((e) => (e[name] ? { ...e, [name]: undefined } : e));
  }, []);
  return { values, errors, set, setErrors };
}

export const fieldId = (name: string) => `g-${name}`;

// Fokus auf das erste Feld mit Fehler (in der Reihenfolge des Formulars)
export function focusFirstError(order: string[], errors: FormErrors) {
  const first = order.find((n) => errors[n]);
  if (first) document.getElementById(fieldId(first))?.focus();
}

function Label({ label, required }: { label: string; required?: boolean }) {
  return (
    <span>
      {label}
      {required && (
        <>
          {' '}
          <span className="req" aria-hidden="true">
            *
          </span>
        </>
      )}
    </span>
  );
}

// extra: z. B. ein Link hinter der Meldung („Zur Anmeldung“)
function ErrorText({ name, f, extra }: { name: string; f: FormState; extra?: ReactNode }) {
  const err = f.errors[name];
  return err ? (
    <p className="ferr" id={`${fieldId(name)}-err`}>
      {err}
      {extra}
    </p>
  ) : null;
}

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'name' | 'value' | 'onChange' | 'id'>;

export function TextField({
  f,
  name,
  label,
  required,
  errorExtra,
  ...rest
}: { f: FormState; name: string; label: string; errorExtra?: ReactNode } & InputProps) {
  const id = fieldId(name);
  const err = f.errors[name];
  return (
    <div className="fld">
      <label htmlFor={id}>
        <Label label={label} required={required} />
        <input
          id={id}
          name={name}
          value={f.values[name] ?? ''}
          onChange={(e) => f.set(name, e.target.value)}
          required={required}
          aria-invalid={err ? true : undefined}
          aria-describedby={err ? `${id}-err` : undefined}
          {...rest}
        />
      </label>
      <ErrorText name={name} f={f} extra={errorExtra} />
    </div>
  );
}

// Vorschläge zur Eingabe: zuerst, was mit dem Getippten (oder einem Wort darin) anfängt, dann was es irgendwo enthält.
// Groß/klein, Umlaute und Satzzeichen zählen nicht („tk“ findet „Techniker Krankenkasse (TK)“). Leer: alle.
const plain = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

export function suggest(options: readonly string[], query: string): string[] {
  const q = plain(query);
  if (!q) return [...options];
  const starts: string[] = [];
  const inside: string[] = [];
  for (const o of options) {
    const p = plain(o);
    if (p.startsWith(q) || p.includes(` ${q}`)) starts.push(o);
    else if (p.replace(/ /g, '').includes(q.replace(/ /g, ''))) inside.push(o);
  }
  return [...starts, ...inside];
}

// Textfeld mit Vorschlägen beim Tippen (z. B. Krankenkasse). Eigene Eingaben gehen weiterhin, ein Vorschlag füllt das
// Feld nur aus. Bedienung wie ein Kombinationsfeld: Pfeiltasten wählen, Enter übernimmt, Escape schließt die Liste.
export function SuggestField({
  f,
  name,
  label,
  required,
  options,
  ...rest
}: { f: FormState; name: string; label: string; options: readonly string[] } & InputProps) {
  const id = fieldId(name);
  const listId = `${id}-list`;
  const err = f.errors[name];
  const value = f.values[name] ?? '';
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const hits = suggest(options, value);
  const exact = hits.length === 1 && plain(hits[0]!) === plain(value);
  const shown = open && hits.length > 0 && !exact;

  useEffect(() => {
    if (shown && active >= 0) document.getElementById(`${listId}-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [shown, active, listId]);

  const pick = (o: string) => {
    f.set(name, o);
    setOpen(false);
    setActive(-1);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!hits.length) return;
      e.preventDefault();
      const down = e.key === 'ArrowDown';
      if (!shown) {
        setOpen(true);
        setActive(down ? 0 : hits.length - 1);
      } else {
        setActive((a) => (down ? (a + 1) % hits.length : a <= 0 ? hits.length - 1 : a - 1));
      }
    } else if (e.key === 'Enter' && shown && active >= 0) {
      e.preventDefault();
      pick(hits[active]!);
    } else if (e.key === 'Escape' && shown) {
      e.preventDefault();
      setOpen(false);
      setActive(-1);
    }
  };

  return (
    <div className="fld sg">
      <label htmlFor={id}>
        <Label label={label} required={required} />
        <input
          id={id}
          name={name}
          value={value}
          onChange={(e) => {
            f.set(name, e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            setOpen(false);
            setActive(-1);
          }}
          onKeyDown={onKeyDown}
          required={required}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={shown}
          aria-controls={listId}
          aria-activedescendant={shown && active >= 0 ? `${listId}-${active}` : undefined}
          aria-invalid={err ? true : undefined}
          aria-describedby={err ? `${id}-err` : undefined}
          autoComplete="off"
          {...rest}
        />
      </label>
      {shown && (
        <ul className="sugg" id={listId} role="listbox" aria-label={t('Vorschläge')}>
          {hits.map((o, i) => (
            <li
              key={o}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              // Maus/Finger: Fokus bleibt im Feld, sonst schlösse die Liste vor dem Klick
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(o)}
            >
              {o}
            </li>
          ))}
        </ul>
      )}
      <ErrorText name={name} f={f} />
    </div>
  );
}

type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'name' | 'value' | 'onChange' | 'id'>;

export function SelectField({
  f,
  name,
  label,
  required,
  options,
  placeholder,
  ...rest
}: { f: FormState; name: string; label: string; options: readonly string[]; placeholder?: string } & SelectProps) {
  const id = fieldId(name);
  const err = f.errors[name];
  return (
    <div className="fld">
      <label htmlFor={id}>
        <Label label={label} required={required} />
        <select
          id={id}
          name={name}
          value={f.values[name] ?? ''}
          onChange={(e) => f.set(name, e.target.value)}
          required={required}
          aria-invalid={err ? true : undefined}
          aria-describedby={err ? `${id}-err` : undefined}
          {...rest}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((o) => (
            <option key={o} value={o}>
              {t(o)}
            </option>
          ))}
        </select>
      </label>
      <ErrorText name={name} f={f} />
    </div>
  );
}

// Abschnitt „Firma“
export function FirmFields({ f }: { f: FormState }) {
  return (
    <>
      <h3>{t('Firma')}</h3>
      <TextField f={f} name="name" label={t('Firmenname')} required maxLength={160} autoComplete="organization" />
      <SelectField
        f={f}
        name="legalForm"
        label={t('Rechtsform')}
        required
        options={LEGAL_FORMS}
        placeholder={t('Bitte wählen')}
      />
      <TextField f={f} name="street" label={t('Straße und Hausnummer')} required maxLength={160} autoComplete="street-address" />
      <div className="two">
        <TextField
          f={f}
          name="zip"
          label={t('PLZ')}
          required
          inputMode="numeric"
          pattern="\d{5}"
          maxLength={5}
          title={t('5-stellige Postleitzahl')}
          autoComplete="postal-code"
        />
        <TextField f={f} name="city" label={t('Ort')} required maxLength={160} autoComplete="address-level2" />
      </div>
      <div className="two">
        <TextField f={f} name="taxNumber" label={t('Steuernummer')} placeholder={t('falls vorhanden')} maxLength={160} />
        <TextField f={f} name="vatId" label={t('USt-IdNr.')} placeholder={t('falls vorhanden')} maxLength={160} />
      </div>
      <TextField f={f} name="employees" label={t('Anzahl Mitarbeiter')} type="number" inputMode="numeric" min={0} step={1} />
    </>
  );
}

// Abschnitt „Bankverbindung“: freiwillig, die Kanzlei kann sie später ergänzen
export function BankFields({ f }: { f: FormState }) {
  return (
    <>
      <h3>{t('Bankverbindung')}</h3>
      <TextField f={f} name="accountHolder" label={t('Kontoinhaber')} placeholder={t('falls vorhanden')} maxLength={160} />
      <TextField
        f={f}
        name="iban"
        label="IBAN"
        placeholder="DE00 0000 0000 0000 0000 00"
        autoCapitalize="characters"
        spellCheck={false}
        autoComplete="off"
        maxLength={42}
      />
      <div className="two">
        <TextField f={f} name="bic" label="BIC" maxLength={160} autoCapitalize="characters" spellCheck={false} />
        <TextField f={f} name="bankName" label={t('Bank')} maxLength={160} />
      </div>
    </>
  );
}

// Verweis auf AGB bzw. Datenschutzerklärung (neuer Tab). Ohne eingestellte Adresse nur der Text.
function DocLink({ href, children }: { href: string | null | undefined; children: string }) {
  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ) : (
    <>{children}</>
  );
}

// Häkchen für Unternehmer, AGB (mit Stand) und AV-Vertrag (Art. 28 DSGVO), alle Pflicht. Die Datenschutzerklärung ist
// nur ein Hinweis, kein Häkchen (Art. 13 DSGVO). Die Adressen kommen aus AGB_URL, AV_URL und DATENSCHUTZ_URL. Die
// Zustimmung ist rechtlich verbindlich und bleibt Deutsch; in anderen Sprachen steht die Übersetzung darunter.
export function AgbField({ f }: { f: FormState }) {
  const config = useConfig();
  const de = currentLang() === 'de';
  const stand = config.agbStand ? ` (Stand ${config.agbStand})` : '';
  return (
    <>
      <Consent
        f={f}
        name="unternehmer"
        translation={de ? null : t('Ich melde die Firma als Unternehmen an, nicht als Privatperson.')}
      >
        Ich melde die Firma als Unternehmen an, nicht als Privatperson.
      </Consent>
      <Consent f={f} name="agb" translation={de ? null : t('Ich akzeptiere die AGB.') + stand}>
        Ich akzeptiere die <DocLink href={config.agbUrl}>AGB</DocLink>
        {stand}.
      </Consent>
      <Consent
        f={f}
        name="av"
        translation={de ? null : t('Ich schließe den Vertrag zur Auftragsverarbeitung (AV-Vertrag nach Art. 28 DSGVO) ab.')}
      >
        Ich schließe den <DocLink href={config.avUrl}>Vertrag zur Auftragsverarbeitung</DocLink> (AV-Vertrag nach Art. 28 DSGVO)
        ab.
      </Consent>
      {!de && <small className="fh">{t('Übersetzung zur Information. Verbindlich ist der deutsche Text.')}</small>}
      <p className="fh">
        {t(
          'Nach dem Absenden prüft Ihre Kanzlei die Angaben. Der Vertrag kommt erst mit der Freischaltung zustande, dazu bekommen Sie eine E-Mail. Vertragssprache ist Deutsch.',
        )}
      </p>
      <p className="fh">
        {t('Wie wir mit Ihren Daten umgehen:')} <DocLink href={config.datenschutzUrl}>{t('Hinweise zum Datenschutz')}</DocLink>
      </p>
    </>
  );
}

function Consent({
  f,
  name,
  translation,
  children,
}: {
  f: FormState;
  name: string;
  translation: string | null;
  children: ReactNode;
}) {
  const id = fieldId(name);
  const err = f.errors[name];
  return (
    <div className="fld">
      <label className="chk" htmlFor={id}>
        <input
          type="checkbox"
          id={id}
          checked={f.values[name] === 'ja'}
          onChange={(e) => f.set(name, e.target.checked ? 'ja' : '')}
          required
          aria-invalid={err ? true : undefined}
          aria-describedby={err ? `${id}-err` : undefined}
        />
        <span lang="de">
          {children}{' '}
          <span className="req" aria-hidden="true">
            *
          </span>
        </span>
      </label>
      {translation && <small className="fh">{translation}</small>}
      <ErrorText name={name} f={f} />
    </div>
  );
}

export const COMPANY_ORDER = ['name', 'legalForm', 'street', 'zip', 'city', 'taxNumber', 'vatId', 'employees'];
export const BANK_ORDER = ['accountHolder', 'iban', 'bic', 'bankName'];

// Prüft Firma, Bankverbindung, Unternehmer, AGB und AV-Vertrag (Meldungen in der gewählten Sprache)
export function validateCompany(v: FormValues): FormErrors {
  const e: FormErrors = {};
  const empty = (k: string) => !(v[k] ?? '').trim();
  if (empty('name')) e.name = t('Bitte geben Sie den Firmennamen ein.');
  if (empty('legalForm')) e.legalForm = t('Bitte wählen Sie die Rechtsform.');
  if (empty('street')) e.street = t('Bitte geben Sie Straße und Hausnummer ein.');
  if (empty('zip')) e.zip = t('Bitte geben Sie die PLZ ein.');
  else if (!zipOk(v.zip ?? '')) e.zip = t('Bitte eine 5-stellige PLZ eingeben.');
  if (empty('city')) e.city = t('Bitte geben Sie den Ort ein.');
  if (!empty('employees') && !/^\d{1,7}$/.test((v.employees ?? '').trim())) e.employees = t('Bitte eine ganze Zahl eingeben.');
  if (!empty('iban') && !ibanOk(v.iban ?? '')) e.iban = t(IBAN_WRONG);
  if (v.unternehmer !== 'ja') e.unternehmer = t('Bitte bestätigen Sie, dass Sie die Firma als Unternehmen anmelden.');
  if (v.agb !== 'ja') e.agb = t('Bitte akzeptieren Sie die AGB.');
  if (v.av !== 'ja') e.av = t('Bitte schließen Sie den AV-Vertrag ab.');
  return e;
}

export const hasErrors = (e: FormErrors) => Object.values(e).some(Boolean);

// Daten für die API (Firma + Bank + Unternehmer, AGB und AV-Vertrag)
export function companyPayload(v: FormValues) {
  const val = (k: string) => (v[k] ?? '').trim();
  return {
    name: val('name'),
    legalForm: val('legalForm'),
    street: val('street'),
    zip: val('zip'),
    city: val('city'),
    taxNumber: val('taxNumber'),
    vatId: val('vatId'),
    employees: val('employees') ? Number(val('employees')) : null,
    accountHolder: val('accountHolder'),
    iban: val('iban'),
    bic: val('bic'),
    bankName: val('bankName'),
    unternehmer: v.unternehmer === 'ja',
    agb: v.agb === 'ja',
    av: v.av === 'ja',
  };
}

// Bekannte Server-Meldungen dem passenden Feld zuordnen (sonst allgemeine Meldung über dem Knopf). Die Meldung kann
// schon übersetzt sein (errorText), dann zählt die Übersetzung des deutschen Textes.
const SERVER_FIELDS: Record<string, string> = {
  [IBAN_WRONG]: 'iban',
  'Bitte eine 5-stellige PLZ eingeben.': 'zip',
  'Für diese E-Mail gibt es schon einen Zugang. Bitte melden Sie sich an.': 'email',
  'Bitte geben Sie eine gültige E-Mail-Adresse ein.': 'email',
  'Das Passwort muss mindestens 8 Zeichen haben.': 'password',
};
export function serverErrorField(message: string): string | null {
  return SERVER_FIELDS[message] ?? Object.entries(SERVER_FIELDS).find(([de]) => t(de) === message)?.[1] ?? null;
}
